/**
 * La couverture d'un cours (docs/09 §3 et §4).
 *
 * L'image est servie par **notre** route de téléchargement et non par une URL
 * de bucket construite ici. Pour une couverture, cette route redirige vers une
 * URL publique et stable — aucune signature, cachable par un CDN — mais c'est
 * elle qui décide, et pas l'écran : le jour où une couverture deviendrait
 * privée, rien à changer ici.
 *
 * Poser une couverture est un geste **à part**, et pas un champ du formulaire de
 * métadonnées. C'est la même raison qui a tenu `status` hors du `PATCH` en
 * phase 3 : cela valide un fichier chez un autre module, écrit un rattachement
 * et en détache un autre. Un champ de formulaire rendrait ces trois écritures
 * invisibles, et le `PATCH` partiel les déclencherait ou non selon qu'un client
 * a envoyé la clé.
 */
import { useTranslation } from "react-i18next";

import { useSetCourseCover } from "@/features/catalog/api/useCatalog";
import { downloadUrl } from "@/features/storage/api/useStorage";
import { FileUploader } from "@/features/storage/components/FileUploader";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";

interface CoverEditorProps {
  courseId: string;
  coverFileId: string | null;
}

export function CoverEditor({ courseId, coverFileId }: CoverEditorProps) {
  const { t } = useTranslation("storage");
  const errorMessage = useApiErrorMessage();
  const setCover = useSetCourseCover(courseId);

  return (
    <Card title={t("cover.title")}>
      {coverFileId !== null && (
        <img
          src={downloadUrl(coverFileId)}
          alt={t("cover.alt")}
          className="mb-3 max-h-48 w-full rounded-md object-cover"
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <FileUploader
          purpose="course_cover"
          label={coverFileId === null ? t("cover.add") : t("upload.replace")}
          onUploaded={(file) => {
            setCover.mutate({ fileId: file.id });
          }}
        />
        {coverFileId !== null && (
          <Button
            variant="ghost"
            disabled={setCover.isPending}
            onClick={() => {
              // `null` **retire** la couverture, et détache le fichier au
              // passage : sans ce détachement, l'enseignant garderait une image
              // que plus aucun cours n'affiche et que le 409 de docs/09 §8
              // l'empêcherait de supprimer.
              setCover.mutate({ fileId: null });
            }}
          >
            {t("cover.remove")}
          </Button>
        )}
      </div>

      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("cover.hint")}</p>
      {setCover.isError && (
        <Alert tone="error" className="mt-3">
          {errorMessage(setCover.error)}
        </Alert>
      )}
    </Card>
  );
}
